import { HotelsList } from '@/components/hotels';
import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import { type Tour } from '@/config/objects/tours';
import { useApiClient, type CrudConfig } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

export function TourHotelsTab() {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApiClient();

  const { data: tourData } = useQuery<{ success: boolean; data: Tour }>({
    queryKey: ['Tour', id],
    queryFn: () => apiClient.get(`/tours/${id}`),
    enabled: !!id,
  });

  const tour = tourData?.data;

  const tourHotelsConfig: CrudConfig<Hotel> = useMemo(
    () => ({
      ...hotelCrudConfig,
      entityName: 'Hotel',
      entityNamePlural: 'Hotels',
      endpoints: {
        ...hotelCrudConfig.endpoints,
        list: `/tours/${id}/hotels`,
        delete: (hotelId: string) => `/tours/${id}/hotels/${hotelId}`,
      },
      listView: {
        ...hotelCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/hotels',
            where: tour
              ? {
                  greaterAreaId: tour.greaterAreaId,
                  tours: {
                    none: {
                      tourId: tour.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (hotel: Hotel) => {
              await apiClient.post(`/tours/${id}/hotels`, { hotelId: hotel.id });
              toast.success('Hotel added to tour');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (hotel: Hotel) => {
              await apiClient.delete(`/tours/${id}/hotels/${hotel.id}`);
              toast.success('Hotel removed from tour');
            },
          },
        },
      },
    }),
    [tour, id, apiClient],
  );

  if (!id) return null;

  return <HotelsList config={tourHotelsConfig} className="p-0" baseUrl="/hotels" />;
}
